import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";

const MODE_CONFIG = {
  Cash: {
    icon: "Banknote",
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  "Bank Deposit": {
    icon: "Building2",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  Cheque: {
    icon: "FileCheck",
    color:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  },
  "Credit Card": {
    icon: "CreditCard",
    color:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  },
  "Debit Card": {
    icon: "CreditCard",
    color:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  },
  "Demand Draft": {
    icon: "FileText",
    color:
      "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  },
  "Online Payment Gateway": {
    icon: "Globe",
    color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
  },
  "Online Transfer": {
    icon: "ArrowRightLeft",
    color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  },
  PayPal: {
    icon: "Wallet",
    color: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  },
  "Wire Transfer": {
    icon: "Zap",
    color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  },
};

const AVATAR_COLORS = [
  "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
];

const money = (value) =>
  Number(value || 0).toLocaleString("en-SA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const RefundsTable = ({ refunds, onVoid }) => {
  if (!refunds.length) return null;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {[
                "Date",
                "Client",
                "Mode",
                "Amount (SR)",
                "Reference",
                "Status",
                "Actions",
              ].map((h) => (
                <th
                  key={h}
                  className="px-5 py-4 text-left text-sm font-medium text-foreground whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {refunds.map((refund, index) => (
              <RefundRow
                key={refund.id}
                refund={refund}
                index={index}
                onVoid={onVoid}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RefundRow = ({ refund, index, onVoid }) => {
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);

  const isVoided = refund.status === "VOID";

  const modeConfig = MODE_CONFIG[refund.refundMode] || {
    icon: "CircleDollarSign",
    color: "bg-gray-100 text-gray-600",
  };

  const initials = (refund.clientName || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        <td className="px-5 py-4 text-sm text-foreground whitespace-nowrap">
          {refund.date
            ? new Date(refund.date).toLocaleDateString("en-SA", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "—"}
        </td>

        <td className="px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor}`}
            >
              {initials}
            </div>
            <span className="text-sm font-medium text-foreground">
              {refund.clientName || "—"}
            </span>
          </div>
        </td>

        <td className="px-5 py-4">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${modeConfig.color}`}
          >
            <Icon name={modeConfig.icon} size={11} />
            {refund.refundMode || "—"}
          </span>
        </td>

        <td className="px-5 py-4">
          <span className="font-semibold text-red-500">
            − SR {money(refund.amount)}
          </span>
        </td>

        <td className="px-5 py-4">
          <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded text-foreground">
            {refund.reference || "—"}
          </span>
        </td>

        <td className="px-5 py-4">
          <span
            className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
              isVoided
                ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                : "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300"
            }`}
          >
            {isVoided ? "VOID" : "RECORDED"}
          </span>
        </td>

        <td className="px-5 py-4">
          <div className="flex items-center gap-1">
            <ActionBtn
              icon="Download"
              label="Download Receipt"
              color="text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20"
              onClick={() => toast("Download coming soon")}
            />

            {!isVoided && (
              <ActionBtn
                icon="RotateCcw"
                label="Void Refund"
                color="text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                onClick={() => setShowVoidConfirm(true)}
              />
            )}
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showVoidConfirm && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td
              colSpan={7}
              className="px-5 py-3 bg-orange-50 dark:bg-orange-900/10 border-b border-orange-200 dark:border-orange-800"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-orange-700 dark:text-orange-300 font-medium">
                  Void refund of <strong>SR {money(refund.amount)}</strong> for{" "}
                  <strong>{refund.clientName}</strong>? This will reverse its
                  accounting entry.
                </span>

                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => setShowVoidConfirm(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => {
                      onVoid(refund.id, "Voided from refunds screen");
                      setShowVoidConfirm(false);
                    }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                  >
                    Void Refund
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

const ActionBtn = ({ icon, label, color, onClick }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      className={`p-1.5 rounded-lg transition-colors ${color}`}
      title={label}
    >
      <Icon name={icon} size={14} />
    </button>

    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

export default RefundsTable;
